import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useDatasets } from "../context/DatasetContext";
import SummaryPanel from "../components/data-table/SummaryPanel";
import { FilterToggle, FilterPanel } from "../components/data-table/FilterPanel";
import DonationsTable from "../components/data-table/DonationsTable";
import { RedfinModal, GoogleSearchModal } from "../components/data-table/PropertyModal";
import { useDonationData } from "../hooks/useDonationData";
import { ProgressPanel } from "../components/ProgressPanel";
import { Database, Filter as FilterIcon, ArrowDownAZ, Rows } from "lucide-react";

function SlowLoadIndicator({ loading, contextLabel }) {
  // Only show the rich progress panel after 2 s of continuous loading, so
  // fast queries (< 2 s) never see it and the UI stays clean.
  const [showPanel, setShowPanel] = useState(false);
  useEffect(() => {
    if (!loading) { setShowPanel(false); return; }
    const t = setTimeout(() => setShowPanel(true), 2000);
    return () => clearTimeout(t);
  }, [loading]);
  if (!showPanel) return null;
  return (
    <ProgressPanel
      variant="block"
      contextLabel={contextLabel}
      phases={[
        { icon: Database,   label: "Querying donations collection",   min: 0 },
        { icon: FilterIcon, label: "Applying filters",                min: 3 },
        { icon: ArrowDownAZ, label: "Sorting results",                min: 6 },
        { icon: Rows,       label: "Preparing page for display",      min: 10 },
      ]}
      reassurances={{
        5:  "Aggregating a large dataset. Almost there…",
        15: "This selection is very large — result is being cached so future queries load instantly.",
      }}
    />
  );
}

export default function DataTablePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debouncedSelectedIds, displayTimeRange } = useDatasets();
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
  const [sortBy, setSortBy] = useState("contribution_amount");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filters, setFilters] = useState({
    year: searchParams.get("year") || "",
    donor: searchParams.get("donor") || "",
    recipient: searchParams.get("recipient") || "",
    address: searchParams.get("address") || "",
    minAmount: searchParams.get("minAmount") || "",
    maxAmount: searchParams.get("maxAmount") || "",
    amountType: searchParams.get("amountType") || "all",
  });
  const [showFilters, setShowFilters] = useState(
    !!(searchParams.get("donor") || searchParams.get("recipient") || searchParams.get("address"))
  );
  const [redfin, setRedfin] = useState(null);
  const [googleSearch, setGoogleSearch] = useState(null);

  const { donations, loading, summary, summaryLoading, topDonors, topRecipients } = useDonationData({
    filters, pagination, setPagination, sortBy, sortOrder, debouncedSelectedIds,
  });

  // Sync filters when URL search params change
  useEffect(() => {
    const newDonor = searchParams.get("donor") || "";
    const newRecipient = searchParams.get("recipient") || "";
    const newAddress = searchParams.get("address") || "";
    setFilters(prev => {
      if (prev.donor === newDonor && prev.recipient === newRecipient && prev.address === newAddress) return prev;
      return { ...prev, donor: newDonor, recipient: newRecipient, address: newAddress };
    });
    if (newDonor || newRecipient || newAddress) setShowFilters(true);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchParams]);

  // Update current index when donor/recipient changes
  useEffect(() => {
    if (filters.donor && topDonors.length > 0) {
      setCurrentIndex(topDonors.findIndex(d => d.name.toLowerCase() === filters.donor.toLowerCase()));
    } else if (filters.recipient && topRecipients.length > 0) {
      setCurrentIndex(topRecipients.findIndex(r => r.name.toLowerCase() === filters.recipient.toLowerCase()));
    } else {
      setCurrentIndex(-1);
    }
  }, [filters.donor, filters.recipient, topDonors, topRecipients]);

  const navigateTo = useCallback((direction) => {
    const list = filters.donor ? topDonors : topRecipients;
    const filterKey = filters.donor ? 'donor' : 'recipient';
    if (list.length === 0) return;
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = list.length - 1;
    if (newIndex >= list.length) newIndex = 0;
    handleFilterChange(filterKey, list[newIndex].name);
    setCurrentIndex(newIndex);
  }, [currentIndex, filters.donor, topDonors, topRecipients]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    const newParams = new URLSearchParams(searchParams);
    // "amountType=all" is the default — omit it from the URL to keep the
    // querystring clean for the common case.
    const shouldSet = value && !(key === "amountType" && value === "all");
    if (shouldSet) { newParams.set(key, value); } else { newParams.delete(key); }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ year: "", donor: "", recipient: "", address: "", minAmount: "", maxAmount: "", amountType: "all" });
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchParams({});
  };

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => k === "amountType" ? v !== "all" : v !== "");

  return (
    <>
      <div className="p-4 md:p-8 space-y-4 md:space-y-6" data-testid="data-table-page">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">Data Table</h1>
            <p className="text-sm text-muted-foreground mt-1">Browse and filter donation records</p>
          </div>
          <FilterToggle showFilters={showFilters} setShowFilters={setShowFilters} hasActiveFilters={hasActiveFilters} />
        </div>

        {showFilters && (
          <FilterPanel filters={filters} handleFilterChange={handleFilterChange} clearFilters={clearFilters} hasActiveFilters={hasActiveFilters} />
        )}

        {summaryLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-48 bg-muted animate-pulse rounded-sm" />
            <div className="h-48 bg-muted animate-pulse rounded-sm" />
          </div>
        ) : (
          <SummaryPanel
            summary={summary}
            topDonors={topDonors}
            topRecipients={topRecipients}
            filters={filters}
            currentIndex={currentIndex}
            navigateTo={navigateTo}
            displayTimeRange={displayTimeRange}
            setRedfin={setRedfin}
            setGoogleSearch={setGoogleSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        )}

        <DonationsTable
          donations={donations} loading={loading}
          pagination={pagination} setPagination={setPagination}
          sortBy={sortBy} sortOrder={sortOrder} handleSort={handleSort}
        />

        {/* Only fires after 2s of loading so quick queries stay clean. */}
        <SlowLoadIndicator
          loading={loading}
          contextLabel={debouncedSelectedIds ? `${debouncedSelectedIds.split(',').length} dataset${debouncedSelectedIds.split(',').length > 1 ? 's' : ''} selected` : ""}
        />
      </div>

      {redfin && <RedfinModal address={redfin} onClose={() => setRedfin(null)} />}
      {googleSearch && <GoogleSearchModal search={googleSearch} onClose={() => setGoogleSearch(null)} />}
    </>
  );
}
